import { useCallback } from 'react';
import { ThemeConfig } from '@/types';
import { themePresets } from '@/themes/presets';

export function useTheme() {
  const applyTheme = useCallback((theme: ThemeConfig) => {
    const root = document.documentElement;
    root.style.setProperty('--color-primary', theme.primary_color);
    root.style.setProperty('--color-secondary', theme.secondary_color);
    root.style.setProperty('--color-accent', theme.accent_color);
    root.style.setProperty('--color-surface', theme.background || '#FFFFFF');
    root.style.setProperty('--font-display', `'${theme.font_family}', serif`);
    root.style.setProperty('--font-body', `'${theme.font_family}', sans-serif`);

    // Determine text color based on background brightness
    const bg = theme.background || '#FFFFFF';
    const isDark = isColorDark(bg);
    root.style.setProperty('--color-on-surface', isDark ? '#F9FAFB' : '#1F2937');
    root.style.setProperty('--color-on-primary', '#FFFFFF');
  }, []);

  const resetTheme = useCallback(() => {
    const root = document.documentElement;
    root.style.removeProperty('--color-primary');
    root.style.removeProperty('--color-secondary');
    root.style.removeProperty('--color-accent');
    root.style.removeProperty('--color-surface');
    root.style.removeProperty('--color-on-primary');
    root.style.removeProperty('--color-on-surface');
    root.style.removeProperty('--font-display');
    root.style.removeProperty('--font-body');
  }, []);

  const getPreset = useCallback((presetId: string) => {
    return themePresets.find((p) => p.id === presetId) || themePresets[0];
  }, []);

  const presetToThemeConfig = useCallback((presetId: string): ThemeConfig => {
    const preset = getPreset(presetId);
    return {
      preset: preset.id,
      primary_color: preset.primary_color,
      secondary_color: preset.secondary_color,
      accent_color: preset.accent_color,
      font_family: preset.font_family,
      background: preset.background,
    };
  }, [getPreset]);

  return {
    applyTheme,
    resetTheme,
    getPreset,
    presetToThemeConfig,
    presets: themePresets,
  };
}

function isColorDark(hex: string): boolean {
  const color = hex.replace('#', '');
  const r = parseInt(color.substring(0, 2), 16);
  const g = parseInt(color.substring(2, 4), 16);
  const b = parseInt(color.substring(4, 6), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance < 0.5;
}
